import React, { useRef, useState, useEffect } from 'react';
import { type PageData, type PageType } from '../types/editing';
import PageEditor from './PageEditor';
import { FolderService } from '../services/FolderService';
import { loadAuthenticatedImage } from '../utils/imageLoader';

interface PageProps {
  pageIndex: number;
  filename: string;
  isbn: string;
  imageDimensions: { width: number; height: number };
  showWords?: boolean;
  positionClass?: string;
  pageData?: PageData;
  onPageClick?: (pageData: PageData) => void;
  onWordClick?: (word: any, block: any) => void;
  currentLeftPage?: number;
  currentRightPage?: number;
  showOverlays?: boolean;
  onPageTypeChange?: (pageIndex: number, pageType: PageType) => void;
}

// Helper function to construct image URL with folder parameter
const getImageUrl = (isbn: string, filename: string): string => {
  const baseUrl = `/api/books/${isbn}/images/${filename}`;
  try {
    const folder = FolderService.getFolder();
    if (folder) {
      return FolderService.addFolderToUrl(baseUrl);
    }
  } catch (error) {
    console.error('[Page] Failed to get folder for image URL:', error);
  }
  return baseUrl;
};

const Page: React.FC<PageProps> = ({ 
  pageIndex, 
  filename, 
  isbn, 
  imageDimensions, 
  showWords = false,
  positionClass,
  pageData,
  onPageClick,
  onWordClick,
  currentLeftPage,
  currentRightPage,
  showOverlays = false,
  onPageTypeChange,
}) => {
  const pageRef = useRef<HTMLDivElement>(null);
  // Initialize image URL with folder parameter synchronously
  const [imageUrl, setImageUrl] = useState<string>(() => getImageUrl(isbn, filename));
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  
  // Load authenticated image as blob
  useEffect(() => {
    let currentObjectUrl: string | null = null;
    setIsLoading(true);
    setHasError(false);
    
    const loadImage = async () => {
      try {
        const url = getImageUrl(isbn, filename);
        const objUrl = await loadAuthenticatedImage(url);
        currentObjectUrl = objUrl;
        setObjectUrl(objUrl);
        // Keep isLoading true - will be set to false when image onLoad fires
      } catch (error) {
        console.error(`[Page] Failed to load authenticated image: ${error}`);
        setIsLoading(false);
        setHasError(true);
        // Fallback to regular URL (will likely fail with 401, but at least we tried)
        setObjectUrl(getImageUrl(isbn, filename));
      }
    };
    
    loadImage();
    
    // Cleanup: revoke object URL when component unmounts or URL changes
    return () => {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [isbn, filename]);
  
  // Update image URL if folder changes (for fallback)
  useEffect(() => {
    const updateImageUrl = () => {
      const newUrl = getImageUrl(isbn, filename);
      setImageUrl(newUrl);
    };
    
    // Update on folder change event
    const handleFolderChange = () => {
      updateImageUrl();
    };
    
    window.addEventListener('folderChanged', handleFolderChange);
    updateImageUrl(); // Also update on mount/update
    
    return () => {
      window.removeEventListener('folderChanged', handleFolderChange);
    };
  }, [isbn, filename]);
  
  let computedPositionClass = positionClass || '';
  
  if (!computedPositionClass) {
    const isRightPage = pageIndex % 2 === 1;
    if (isRightPage) {
      if (pageIndex === 1) computedPositionClass = 'current-right-page';
      else if (pageIndex === 3) computedPositionClass = 'next-right-page';
      else if (pageIndex === 5) computedPositionClass = 'future-right-page';
      else computedPositionClass = 'future-right-page';
    } else {
      if (pageIndex === 2) computedPositionClass = 'next-left-page';
      else if (pageIndex === 4) computedPositionClass = 'future-left-page';
      else computedPositionClass = 'future-left-page';
    }
  }

  const basePageClass = computedPositionClass.includes('left') ? 'left-page' : 'right-page';

  const handlePageClick = () => {
    if (pageData && onPageClick && computedPositionClass.includes('current')) {
      onPageClick(pageData);
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div 
      ref={pageRef}
      className={`page ${basePageClass} ${computedPositionClass}`} 
      data-page-index={pageIndex}
      onClick={handlePageClick}
    >
      {isLoading && !hasError && (
        <div className="page-loading-indicator">
          <div className="page-loading-spinner"></div>
        </div>
      )}
      {hasError && (
        <div className="page-error-indicator">
          <div className="page-error-icon">⚠️</div>
          <div className="page-error-text">Failed to load image</div>
        </div>
      )}
      {objectUrl && !hasError && (
        <img 
          src={objectUrl} 
          alt={`Page ${pageIndex}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            opacity: showWords ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
            visibility: isLoading ? 'hidden' : 'visible'
          }}
        />
      )}
      
      {/* Page Editor */}
      <PageEditor
        pageIndex={pageIndex}
        imageDimensions={imageDimensions}
        onWordClick={onWordClick}
        isbn={isbn}
        wordsFilename={pageData?.words || pageData?.['words-enhanced'] || null}
        showWords={showWords}
        pageData={pageData}
        currentLeftPage={currentLeftPage}
        currentRightPage={currentRightPage}
        showOverlays={showOverlays}
        onPageTypeChange={onPageTypeChange}
      />
    </div>
  );
};

export default Page;